import { useTranslation } from 'react-i18next';

import type { IContactDraftFields } from '../../../interfaces/prospects';
import { TRANSLATIONS_GLOBAL } from '../../../translations/commons';
import { TRANSLATIONS_PROSPECTS } from '../../../translations/prospects';
import { validateEmail } from '../../../utils/validation/validation';
import CustomInput from '../../Inputs/CustomInput/CustomInput';

interface IContactFieldsProps {
  draft: IContactDraftFields;
  onChange: (key: keyof IContactDraftFields, value: string) => void;
  generalSectionLabel?: string;
  namePlaceholder: string;
  emailPlaceholder: string;
  emailRequired?: boolean;
  showTitle?: boolean;
  requiredTitle?: boolean;
  nameRequired?: boolean;
  requiredPhone?: boolean;
  disabled?: boolean;
}

const ContactFields = ({
  draft,
  onChange,
  generalSectionLabel = '',
  namePlaceholder,
  emailPlaceholder,
  emailRequired = false,
  showTitle = false,
  requiredTitle = false,
  nameRequired = true,
  requiredPhone = false,
  disabled = false
}: IContactFieldsProps) => {
  const { t } = useTranslation();

  const showErrors = !disabled;

  const requiredLabel = (label: string) => (
    <>
      {label} <span className="profile-form__required">{t(TRANSLATIONS_GLOBAL.required)}</span>
    </>
  );

  return (
    <>
      {generalSectionLabel && <h4 className="profile-form__section-title">{generalSectionLabel}</h4>}
      <div className="profile-form__group">
        <div className="profile-form__row">
          <CustomInput
            label={nameRequired ? requiredLabel(t(TRANSLATIONS_GLOBAL.name)) : t(TRANSLATIONS_GLOBAL.name)}
            placeholder={namePlaceholder}
            value={draft.name}
            error={showErrors && nameRequired && !draft.name.trim()}
            onChange={(event) => onChange('name', event.target.value)}
            disabled={disabled}
            maxLength={50}
          />
          {(showTitle || requiredTitle) && (
            <CustomInput
              label={
                requiredTitle
                  ? requiredLabel(t(TRANSLATIONS_PROSPECTS.profile.fields.title))
                  : t(TRANSLATIONS_PROSPECTS.profile.fields.title)
              }
              placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactTitle.placeholder)}
              value={draft.title}
              error={showErrors && requiredTitle && !draft.title.trim()}
              onChange={(event) => onChange('title', event.target.value)}
              disabled={disabled}
              maxLength={50}
            />
          )}
        </div>
        <div className="profile-form__row">
          <CustomInput
            type="email"
            label={emailRequired ? requiredLabel(t(TRANSLATIONS_GLOBAL.email)) : t(TRANSLATIONS_GLOBAL.email)}
            placeholder={emailPlaceholder}
            value={draft.email}
            error={
              showErrors &&
              ((emailRequired && !draft.email.trim()) || (!!draft.email.trim() && !validateEmail(draft.email.trim())))
            }
            onChange={(event) => onChange('email', event.target.value)}
            disabled={disabled}
          />
          <CustomInput
            type="tel"
            label={requiredPhone ? requiredLabel(t(TRANSLATIONS_GLOBAL.phone)) : t(TRANSLATIONS_GLOBAL.phone)}
            placeholder={t(TRANSLATIONS_PROSPECTS.forms.contactPhone.placeholder)}
            value={draft.phone}
            error={showErrors && requiredPhone && !draft.phone.trim()}
            onChange={(event) => onChange('phone', event.target.value.replace(/\D/g, ''))}
            maxLength={16}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
};

export default ContactFields;
